/**
 * 转发纯逻辑单点（任务 14.8；旧版 forward_page.dart 口径）。
 *
 * 协议实锤：后端没有 HTTP 转发接口——转发 = 对每个目标按原类型重发 WS send
 * 帧，严格串行且帧间隔 200ms（旧版注释：Socket 服务端不接受 20 条消息同时
 * 到达）。上限 20 条（旧版 _maxForwardMessages）。
 *
 * 载荷口径：
 * - 媒体白名单（图片/名片/语音/视频/文件/贴纸）：原 msgType + 原 content 原样
 *   重发——content 自含远端 URL 等全部信息，图片 envelope 里的 kind=sticker
 *   自传表情标记天然保留（旧版转发同样带上）。
 * - 文本：解 envelope 后重建纯文本 envelope——@提及（t/g）与阅后即焚 clock
 *   不随转发（旧版走 sendText 同口径）。
 * - 位置/链接/其他类型：降级为占位文案文本（旧版 _forwardText 同表）。
 * - 媒体资源 URL 为空：降级为对应占位文案。旧版此处 throw 并中止整个转发
 *   循环，新版降级继续发，刻意偏差（更不容易把一批转发整体搞挂）。
 * - **ext 一律不带**：引用（reply）、相册（album_id）等 ext 不随转发——旧版
 *   重建发送参数时同样全部丢弃。
 */

import { MsgType, msgTypeSpec } from './msgTypes';
import {
  parseContactCardEnvelope,
  parseFileEnvelope,
  parseImageEnvelope,
  parseVideoEnvelope,
  parseVoiceEnvelope,
} from './messageModel';
import { buildTextEnvelope, parseTextEnvelope } from './preview';

/** 单次转发消息条数上限（旧版 _maxForwardMessages） */
export const FORWARD_MAX_MESSAGES = 20;
/** 帧间隔（旧版 _forwardInterval；服务端不接受帧风暴） */
export const FORWARD_INTERVAL_MS = 200;

export interface ForwardPayload {
  readonly msgType: number;
  readonly content: string;
}

/**
 * 是否可转发（UI 菜单/多选与 Worker 共用门禁）：服务端已确认的内容消息。
 * id>0 即服务端行（乐观行不进消息 store，天然满足旧版「本人消息须发送成功」
 * 口径）；system-note/信令不可转发。
 * secret（14.13c）：阅后即焚消息一律不可转发/多选——旧版只在焚烧动画期间禁
 * （_burningMessageIds），新版扩成 isSecretMessage 全程禁（转发会把即焚内容
 * 永久化，违背语义），刻意偏差。调用方自算 secret（VM 有字段 / Worker 行上
 * isSecretMessage(row.msg, row.deleteTime)）。
 * encrypted（14.14a）：端到端加密消息同样禁转发——目标会话密钥不同，转发的
 * 密文在对端必是乱码；调用方取行/VM 的 isEncrypted===1。
 */
export function canForwardMessage(
  id: number,
  msgType: number,
  secret = false,
  encrypted = false,
): boolean {
  return !secret && !encrypted && id > 0 && msgTypeSpec(msgType).category === 'content';
}

/** 降级占位文案（旧版 _forwardText 同表；文本类调用方自取原文不走这里） */
function fallbackText(msgType: number): string {
  switch (msgType) {
    case MsgType.Image:
      return '[图片]';
    case MsgType.Voice:
      return '[语音]';
    case MsgType.Video:
      return '[视频]';
    case MsgType.File:
      return '[文件]';
    case MsgType.Card:
      return '[名片]';
    case MsgType.Location:
      return '[位置]';
    case MsgType.Link:
      return '[链接]';
    case MsgType.Sticker:
      return '[表情]';
    default:
      return '[消息]';
  }
}

const degrade = (msgType: number): ForwardPayload => ({
  msgType: MsgType.Text,
  content: buildTextEnvelope(fallbackText(msgType)),
});

/**
 * 由存量行（msgType + content）规划转发载荷。调用方先过 canForwardMessage；
 * 这里只管「按原类型重发还是降级文本」。
 */
export function planForwardPayload(msgType: number, msg: string): ForwardPayload {
  switch (msgType) {
    case MsgType.Text: {
      const text = parseTextEnvelope(msg).content.trim();
      return {
        msgType: MsgType.Text,
        content: buildTextEnvelope(text !== '' ? text : fallbackText(msgType)),
      };
    }
    case MsgType.Image:
      return parseImageEnvelope(msg).url.trim() !== '' ? { msgType, content: msg } : degrade(msgType);
    case MsgType.Voice:
      return parseVoiceEnvelope(msg).url.trim() !== '' ? { msgType, content: msg } : degrade(msgType);
    case MsgType.Video:
      return parseVideoEnvelope(msg).url.trim() !== '' ? { msgType, content: msg } : degrade(msgType);
    case MsgType.File:
      return parseFileEnvelope(msg).url.trim() !== '' ? { msgType, content: msg } : degrade(msgType);
    case MsgType.Card: {
      const card = parseContactCardEnvelope(msg);
      return card.userId > 0 || card.username.trim() !== ''
        ? { msgType, content: msg }
        : degrade(msgType);
    }
    // 贴纸 envelope 形态归 14.16/14.17，这里原样重发即忠实于旧版 sendBuiltinSticker
    case MsgType.Sticker:
      return msg.trim() !== '' ? { msgType, content: msg } : degrade(msgType);
    default:
      return degrade(msgType);
  }
}
